import { API_BASE_URL, apiUrl } from '../../static/env';

function nowIso() {
  return new Date().toISOString();
}

function fmtMs(ms) {
  if (ms == null || Number.isNaN(ms)) return '—';
  return `${Math.round(ms)} мс`;
}

function toText(data) {
  if (data == null) return '';
  try {
    return typeof data === 'string' ? data : JSON.stringify(data, null, 2);
  } catch (e) {
    return String(data);
  }
}

function previewBody(data, maxLen = 400) {
  const text = toText(data);
  if (text.length <= maxLen) return text;
  return `${text.slice(0, maxLen)}… (${text.length} симв.)`;
}

export function maskSensitiveBody(body) {
  if (!body || typeof body !== 'object') return body;
  const copy = { ...body };
  if ('password' in copy) copy.password = '***';
  return copy;
}

/** Подсказка по типичным ошибкам fetch в браузере. */
export function classifyFetchError(err) {
  const name = err?.name || '';
  const msg = (err?.message || String(err)).toLowerCase();

  if (name === 'AbortError') {
    return 'Таймаут: сервер не ответил за отведённое время. Возможны блокировка, обрыв или перегрузка на маршруте.';
  }
  if (msg.includes('load failed')) {
    return 'Load failed (Safari/iOS): HTTP-ответ не получен. Часто — мобильный оператор, DNS, TLS, файрвол до api.*. На сервере запроса может не быть.';
  }
  if (msg.includes('failed to fetch') || msg.includes('networkerror')) {
    return 'Failed to fetch: сеть, CORS, DNS или TLS. Если пинг (/app-version) тоже падает — до API не доходят пакеты.';
  }
  if (msg.includes('cors')) {
    return 'CORS: браузер заблокировал ответ (редко на том же домене API).';
  }
  if (name === 'TypeError') {
    return 'TypeError при fetch: обычно сетевая ошибка до получения HTTP-статуса.';
  }
  return 'Неизвестная ошибка fetch — смотрите тип и текст ниже.';
}

function pickResponseHeaders(response) {
  if (!response?.headers) return {};
  const keys = [
    'date',
    'server',
    'content-type',
    'content-length',
    'cf-ray',
    'x-request-id',
    'x-powered-by',
    'via',
  ];
  const out = {};
  keys.forEach((k) => {
    const v = response.headers.get(k);
    if (v) out[k] = v;
  });
  return out;
}

/**
 * Один HTTP-запрос с замером времени (как строка в логе десктопа).
 */
export async function runTimedRequest({
  label,
  endpoint,
  method = 'POST',
  body,
  timeoutMs = 120000,
  absoluteUrl = null,
}) {
  const requestUrl = absoluteUrl || apiUrl(endpoint);
  const row = {
    label,
    endpoint,
    method,
    requestUrl,
    status: 'waiting',
    startedAt: nowIso(),
    finishedAt: null,
    durationMs: null,
    httpStatus: null,
    ok: null,
    error: null,
    errorType: null,
    errorHint: null,
    errorCause: null,
    responseHeaders: null,
    preview: null,
    bytes: null,
  };

  const t0 = performance.now();
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const init = {
      method,
      signal: controller.signal,
      headers: {},
    };
    if (body != null && method !== 'GET' && method !== 'HEAD') {
      init.headers['Content-Type'] = 'application/json';
      init.body = JSON.stringify(body);
    }

    const response = await fetch(requestUrl, init);
    const t1 = performance.now();
    row.finishedAt = nowIso();
    row.durationMs = t1 - t0;
    row.httpStatus = response.status;
    row.ok = response.ok;
    row.responseHeaders = pickResponseHeaders(response);

    const raw = await response.text();
    row.bytes = raw.length;

    let parsed = null;
    if (raw) {
      try {
        parsed = JSON.parse(raw);
      } catch (e) {
        parsed = raw;
      }
    }

    row.fullResponse = raw || toText(parsed);

    if (!response.ok) {
      row.status = 'error';
      row.error = typeof parsed === 'object' && parsed?.error
        ? String(parsed.error)
        : `HTTP ${response.status}`;
      row.preview = previewBody(parsed);
    } else {
      row.status = 'completed';
      row.preview = previewBody(parsed);
      row.parsed = parsed;
    }
  } catch (err) {
    const t1 = performance.now();
    row.finishedAt = nowIso();
    row.durationMs = t1 - t0;
    row.status = 'error';
    row.errorType = err?.name || 'Error';
    row.error = err?.message || String(err);
    row.errorHint = classifyFetchError(err);
    if (err?.cause) row.errorCause = String(err.cause);
    row.fullResponse = [
      `type: ${row.errorType}`,
      `message: ${row.error}`,
      row.errorCause ? `cause: ${row.errorCause}` : null,
      `hint: ${row.errorHint}`,
    ].filter(Boolean).join('\n');
  } finally {
    clearTimeout(timer);
  }

  return row;
}

export const DIAG_ATTEMPT_TIMEOUT_MS = 10000;
export const DIAG_MAX_ATTEMPTS = 5;
export const DIAG_RETRY_PAUSE_MS = 400;

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function attemptLabel(n) {
  const names = ['', 'Первая', 'Вторая', 'Третья', 'Четвёртая', 'Пятая', 'Шестая', 'Седьмая'];
  return names[n] ? `${names[n]} попытка` : `Попытка ${n}`;
}

function shouldRetryAfterAttempt(row, endpoint) {
  if (row.status === 'completed') {
    if (endpoint === '/login' && row.parsed && row.parsed.error) {
      return false;
    }
    return false;
  }
  return true;
}

function buildRetryAggregate({ label, endpoint, method, requestUrl, requestBody, attempts, overallStarted }) {
  const last = attempts[attempts.length - 1];
  const success = attempts.find((a) => {
    if (a.status !== 'completed') return false;
    if (endpoint === '/login' && a.parsed?.error) return false;
    return true;
  });
  const winner = success || last;
  const finishedAt = last?.finishedAt || nowIso();
  const t0 = attempts[0]?.startedAt ? Date.parse(attempts[0].startedAt) : null;
  const t1 = finishedAt ? Date.parse(finishedAt) : null;
  const totalMs = t0 != null && t1 != null ? t1 - t0 : attempts.reduce((s, a) => s + (a.durationMs || 0), 0);

  return {
    label,
    endpoint,
    method,
    requestUrl: requestUrl || winner?.requestUrl,
    requestBody,
    status: success ? 'completed' : 'error',
    startedAt: overallStarted,
    finishedAt,
    durationMs: totalMs,
    httpStatus: winner?.httpStatus ?? null,
    bytes: winner?.bytes ?? null,
    error: success ? null : winner?.error,
    errorType: success ? null : winner?.errorType,
    errorHint: success ? null : winner?.errorHint,
    errorCause: success ? null : winner?.errorCause,
    responseHeaders: winner?.responseHeaders ?? null,
    preview: winner?.preview ?? null,
    fullResponse: winner?.fullResponse ?? null,
    parsed: winner?.parsed,
    attempts,
    maxAttempts: attempts.length,
    successAttempt: success ? success.attemptNumber : null,
    retryNote: success
      ? `Успех на ${attemptLabel(success.attemptNumber).toLowerCase()} (${success.attemptNumber}/${attempts.length})`
      : `Все ${attempts.length} попыток неудачны (таймаут ${DIAG_ATTEMPT_TIMEOUT_MS} мс каждая)`,
  };
}

/**
 * Несколько попыток с коротким таймаутом; onProgress — обновление UI между попытками.
 */
export async function runTimedRequestWithRetries({
  label,
  endpoint,
  method = 'POST',
  body,
  maxAttempts = DIAG_MAX_ATTEMPTS,
  attemptTimeoutMs = DIAG_ATTEMPT_TIMEOUT_MS,
  pauseMs = DIAG_RETRY_PAUSE_MS,
  onProgress,
  absoluteUrl = null,
}) {
  const requestUrl = absoluteUrl || apiUrl(endpoint);
  const overallStarted = nowIso();
  const attempts = [];

  for (let n = 1; n <= maxAttempts; n += 1) {
    const progressBase = {
      label,
      endpoint,
      method,
      requestUrl,
      status: 'waiting',
      currentAttempt: n,
      maxAttempts,
      attemptLabel: attemptLabel(n),
      attempts: [...attempts],
      startedAt: overallStarted,
      finishedAt: null,
      retryNote: n > 1 ? attemptLabel(n) : null,
    };
    if (onProgress) onProgress(progressBase);

    const row = await runTimedRequest({
      label,
      endpoint,
      method,
      body,
      timeoutMs: attemptTimeoutMs,
      absoluteUrl,
    });
    row.attemptNumber = n;
    row.attemptLabel = attemptLabel(n);
    attempts.push(row);

    if (onProgress) {
      onProgress({
        ...progressBase,
        attempts: [...attempts],
        status: row.status === 'completed' && !shouldRetryAfterAttempt(row, endpoint)
          ? 'completed'
          : (n < maxAttempts && shouldRetryAfterAttempt(row, endpoint) ? 'retrying' : row.status),
        finishedAt: row.finishedAt,
        durationMs: row.durationMs,
        httpStatus: row.httpStatus,
        preview: row.preview,
        error: row.error,
        errorHint: row.errorHint,
        retryNote: n < maxAttempts && shouldRetryAfterAttempt(row, endpoint)
          ? `${attemptLabel(n)} — неудача, далее ${attemptLabel(n + 1).toLowerCase()}…`
          : null,
      });
    }

    if (!shouldRetryAfterAttempt(row, endpoint)) {
      break;
    }
    if (n < maxAttempts) {
      await sleep(pauseMs);
    }
  }

  return buildRetryAggregate({
    label,
    endpoint,
    method,
    requestUrl,
    requestBody: null,
    attempts,
    overallStarted,
  });
}

/** Геолокация по IP + лог попыток по каждому сервису. */
export async function fetchGeoInfo() {
  const providers = [
    {
      name: 'ipapi.co',
      url: 'https://ipapi.co/json/',
      map: (d) => ({
        ip: d.ip,
        city: d.city,
        region: d.region,
        country: d.country_name,
        isp: d.org,
        timezone: d.timezone,
      }),
    },
    {
      name: 'ipwho.is',
      url: 'https://ipwho.is/',
      map: (d) => ({
        ip: d.ip,
        city: d.city,
        region: d.region,
        country: d.country,
        isp: d.connection?.isp,
        timezone: d.timezone?.id,
      }),
    },
    {
      name: 'ipify+ipapi',
      url: 'https://api.ipify.org?format=json',
      map: (d) => ({ ip: d.ip }),
      ipOnly: true,
    },
  ];

  const attempts = [];

  for (const p of providers) {
    const attempt = { provider: p.name, ok: false, durationMs: null, error: null };
    try {
      const t0 = performance.now();
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 10000);
      const res = await fetch(p.url, { signal: controller.signal });
      clearTimeout(timer);
      attempt.durationMs = performance.now() - t0;
      attempt.httpStatus = res.status;

      if (!res.ok) {
        attempt.error = `HTTP ${res.status}`;
        attempts.push(attempt);
        continue;
      }

      const data = await res.json();
      if (p.ipOnly && data.ip) {
        try {
          const c2 = new AbortController();
          const t2 = setTimeout(() => c2.abort(), 8000);
          const geoRes = await fetch(`https://ipapi.co/${data.ip}/json/`, { signal: c2.signal });
          clearTimeout(t2);
          const geo = await geoRes.json();
          const mapped = {
            ip: data.ip,
            city: geo.city,
            region: geo.region,
            country: geo.country_name,
            isp: geo.org,
            timezone: geo.timezone,
          };
          if (mapped.ip) {
            return { provider: p.name, durationMs: attempt.durationMs, attempts, ...mapped, error: null };
          }
        } catch (e2) {
          return {
            provider: p.name,
            durationMs: attempt.durationMs,
            attempts,
            ip: data.ip,
            city: null,
            region: null,
            country: null,
            isp: null,
            timezone: null,
            error: null,
            ipOnlyResolved: true,
          };
        }
      }

      const mapped = p.map(data);
      attempt.ok = Boolean(mapped.ip || mapped.city);
      attempts.push(attempt);
      if (mapped.ip || mapped.city) {
        return {
          provider: p.name,
          durationMs: attempt.durationMs,
          attempts,
          ...mapped,
          error: null,
        };
      }
      attempt.error = 'пустой ответ';
    } catch (e) {
      attempt.error = e?.message || String(e);
      attempt.durationMs = attempt.durationMs ?? null;
      attempts.push(attempt);
    }
  }

  return {
    provider: null,
    error: 'Не удалось определить город по IP (все сервисы недоступны)',
    ip: null,
    city: null,
    region: null,
    country: null,
    isp: null,
    timezone: null,
    attempts,
  };
}

export function getClientEnvInfo() {
  const nav = typeof navigator !== 'undefined' ? navigator : {};
  const conn = nav.connection || nav.mozConnection || nav.webkitConnection;
  return {
    apiBase: API_BASE_URL,
    pageUrl: typeof window !== 'undefined' ? window.location.href : '',
    userAgent: nav.userAgent || '',
    language: nav.language || '',
    onLine: nav.onLine !== false,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    screen: typeof window !== 'undefined'
      ? `${window.screen?.width}x${window.screen?.height}`
      : '',
    connection: conn ? {
      effectiveType: conn.effectiveType,
      rtt: conn.rtt,
      downlink: conn.downlink,
      saveData: conn.saveData,
    } : null,
  };
}

export function buildConnectivityVerdict(steps) {
  const ping = steps.find((s) => s.key === 'ping_app_version');
  const login = steps.find((s) => s.key === 'login');
  if (!ping && !login) return null;

  const lines = [];
  if (ping?.status === 'completed') {
    lines.push('✓ GET /app-version — API достижим (TCP+TLS+HTTP OK).');
  } else if (ping?.status === 'error') {
    lines.push('✗ GET /app-version — до API не дошли. Проблема сети/DNS/файрвол до хостинга.');
  }

  if (login?.status === 'completed') {
    const att = login.successAttempt
      ? ` (успех с ${login.successAttempt}-й попытки из ${login.attempts?.length || '?'})`
      : '';
    lines.push(`✓ POST /login — авторизация OK${att}.`);
  } else if (login?.status === 'error') {
    if (ping?.status === 'completed') {
      lines.push('✗ POST /login упал, но пинг прошёл — смотрите HTTP-код/тело login (не «Load failed»).');
    } else {
      lines.push('✗ POST /login — как пинг: вероятно запрос не доходит до сервера.');
    }
    if (login.errorHint) lines.push(`  Подсказка: ${login.errorHint}`);
    if (login.attempts?.length > 1) {
      lines.push(`  Попыток login: ${login.attempts.length}, все неудачны.`);
    }
  }

  return lines.join('\n');
}

export function buildDiagReport({ phone, envInfo, geoInfo, summary, steps, verdict }) {
  const lines = [
    '=== Диагностика API ===',
    `Сформировано: ${nowIso()}`,
    `Логин: ${phone || '—'}`,
    '',
  ];

  if (envInfo) {
    lines.push('--- Окружение ---', toText(envInfo), '');
  }

  if (geoInfo) {
    lines.push('--- Локация ---');
    if (geoInfo.error && !geoInfo.ip) {
      lines.push(geoInfo.error);
    } else {
      lines.push(
        `IP: ${geoInfo.ip || '—'}`,
        `Город: ${geoInfo.city || '—'}`,
        `Регион: ${geoInfo.region || '—'}`,
        `Страна: ${geoInfo.country || '—'}`,
        `Провайдер: ${geoInfo.isp || '—'}`,
        `Часовой пояс: ${geoInfo.timezone || '—'}`,
        `Сервис: ${geoInfo.provider || '—'}`,
        `Запрос: ${fmtMs(geoInfo.durationMs)}`,
      );
    }
    if (geoInfo.attempts?.length) {
      lines.push('', 'Попытки geo:');
      geoInfo.attempts.forEach((a) => {
        lines.push(`  ${a.provider}: ${a.ok ? 'ok' : 'fail'} ${a.error || ''} ${fmtMs(a.durationMs)} HTTP ${a.httpStatus ?? '—'}`);
      });
    }
    lines.push('');
  }

  if (verdict) {
    lines.push('--- Вывод ---', verdict, '');
  }

  if (summary) {
    const summaryLines = [
      '--- Итог ---',
      `user_id: ${summary.userId ?? '—'}`,
      `сообщений в fast-ответе: ${summary.messageCount}`,
      summary.loginAttempts != null
        ? `попыток login: ${summary.loginAttempts}${summary.loginSuccessAttempt ? `, успех с ${summary.loginSuccessAttempt}-й` : ''}`
        : null,
      `ошибки: ${summary.errors?.length ? summary.errors.join(', ') : 'нет'}`,
      `завершено: ${summary.finishedAt}`,
      '',
    ].filter((line) => line != null);
    lines.push(...summaryLines);
  }

  if (steps?.length) {
    lines.push('--- Запросы ---');
    steps.forEach((step, i) => {
      lines.push('');
      lines.push(`[${i + 1}] ${step.label}`);
      lines.push(`  ${step.method} ${step.endpoint}`);
      if (step.requestUrl) lines.push(`  URL: ${step.requestUrl}`);
      lines.push(`  Статус: ${step.status}`);
      if (step.startedAt) lines.push(`  Старт (отправлен): ${step.startedAt}`);
      if (step.finishedAt) lines.push(`  Финиш: ${step.finishedAt}`);
      if (step.durationMs != null) lines.push(`  Длительность: ${fmtMs(step.durationMs)}`);
      if (step.httpStatus != null) lines.push(`  HTTP: ${step.httpStatus}`);
      if (step.bytes != null) lines.push(`  Байт: ${step.bytes}`);
      if (step.retryNote) lines.push(`  Итог попыток: ${step.retryNote}`);
      if (step.attempts?.length) {
        lines.push('  Попытки:');
        step.attempts.forEach((a) => {
          lines.push(
            `    ${a.attemptLabel || `Попытка ${a.attemptNumber}`}: ${a.status} `
            + `${fmtMs(a.durationMs)} HTTP ${a.httpStatus ?? '—'} `
            + `${a.error || (a.preview ? 'ok' : '')}`
          );
        });
      }
      if (step.requestBody) lines.push(`  Тело запроса: ${step.requestBody}`);
      if (step.errorType) lines.push(`  Тип ошибки: ${step.errorType}`);
      if (step.error) lines.push(`  Ошибка: ${step.error}`);
      if (step.errorHint) lines.push(`  Подсказка: ${step.errorHint}`);
      if (step.errorCause) lines.push(`  Cause: ${step.errorCause}`);
      if (step.responseHeaders && Object.keys(step.responseHeaders).length) {
        lines.push(`  Заголовки ответа: ${toText(step.responseHeaders)}`);
      }
      if (step.fullResponse) lines.push(`  Ответ:\n${step.fullResponse}`);
      else if (step.preview) lines.push(`  Ответ (превью): ${step.preview}`);
    });
  }

  return lines.join('\n');
}

export function downloadTextFile(filename, text) {
  const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export { fmtMs, previewBody, nowIso, toText };
